// Token storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Function to get tokens from sessionStorage
// Note: Cookies are HttpOnly so we can't read them from JavaScript
// But the browser sends them automatically, so API calls will work even without sessionStorage
const getAccessToken = (): string | null => {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = (): string | null => {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

// Function to set tokens in sessionStorage
const setTokens = (accessToken: string, refreshToken: string): void => {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

// Function to clear tokens
const clearTokens = (): void => {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Helper function to redirect to login page
const redirectToLogin = (): void => {
    const currentPath = window.location.pathname.toLowerCase();
    const loginPath = '/auth/login';
    
    // Only redirect if not already on login page or error page
    /*if (currentPath !== loginPath && 
        !currentPath.startsWith('/home/error') && 
        currentPath !== '/home/error') {
        window.location.href = loginPath;
    }*/
};

// Token refresh state
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Function to refresh access token
async function refreshAccessToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            const refreshToken = getRefreshToken();
            if (!refreshToken) {
                return null;
            }

            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.succeeded && data.data) {
                    setTokens(data.data.accessToken, data.data.refreshToken);
                    return data.data.accessToken;
                }
            } else {
                // Refresh failed, clear tokens but don't redirect if on error page
                clearTokens();
                // Redirect to login page if not already there or on error page
                // Error page has login button, so don't redirect from there
                redirectToLogin();
                return null;
            }
        } catch (error) {
            clearTokens();
            // Don't redirect if on error page (error page has login button)
            redirectToLogin();
            return null;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

// Sliding refresh — keeps the 15-min access token/cookie alive as long as the tab is open.
// The server issues access tokens for 15 minutes; we refresh at a shorter interval so the
// cookie never expires from user inactivity alone.
const SLIDING_REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 minutes
let slidingRefreshTimer: ReturnType<typeof setInterval> | null = null;
let slidingRefreshVisibilityHandler: (() => void) | null = null;

function startSlidingRefresh(): void {
    if (slidingRefreshTimer !== null) return; // already running
    if (!getRefreshToken()) return; // not logged in — nothing to keep alive

    const tick = async () => {
        if (!getRefreshToken()) {
            stopSlidingRefresh();
            return;
        }
        try {
            await refreshAccessToken();
        } catch {
            // refreshAccessToken handles its own cleanup; swallow so the timer survives network blips.
        }
    };

    slidingRefreshTimer = setInterval(tick, SLIDING_REFRESH_INTERVAL_MS);

    // When the tab returns to the foreground after being hidden (possibly past the token's
    // lifetime), refresh immediately instead of waiting up to 12 minutes for the next tick.
    slidingRefreshVisibilityHandler = () => {
        if (document.visibilityState === 'visible') {
            void tick();
        }
    };
    document.addEventListener('visibilitychange', slidingRefreshVisibilityHandler);
}

function stopSlidingRefresh(): void {
    if (slidingRefreshTimer !== null) {
        clearInterval(slidingRefreshTimer);
        slidingRefreshTimer = null;
    }
    if (slidingRefreshVisibilityHandler) {
        document.removeEventListener('visibilitychange', slidingRefreshVisibilityHandler);
        slidingRefreshVisibilityHandler = null;
    }
}

export interface ApiResponse<T> {
    succeeded: boolean;
    data?: T;
    message?: string;
    errors?: Array<{ code?: string; message?: string }>;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    let accessToken = getAccessToken();
    const headers = new Headers(options.headers || {});

    if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const config: RequestInit = {
        ...options,
        headers,
    };
    
    const fullUrl = `/api${url}`;
    let response = await fetch(fullUrl, config);

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401) {
        if (accessToken) {
            const newAccessToken = await refreshAccessToken();
            
            if (newAccessToken) {
                // Retry original request with new token
                headers.set('Authorization', `Bearer ${newAccessToken}`);
                response = await fetch(fullUrl, {
                    ...options,
                    headers
                });
            } else {
                // Refresh failed - clear tokens but don't redirect if on error page
                clearTokens();
                // Only redirect if not on error page (error page has login button)
                const currentPath = window.location.pathname.toLowerCase();
                if (!currentPath.startsWith('/home/error') && currentPath !== '/home/error') {
                    redirectToLogin();
                }
                const error: any = new Error('Authentication failed. Please login again.');
                error.response = {
                    status: 401,
                    data: { succeeded: false, message: 'Authentication failed. Please login again.' }
                };
                throw error;
            }
        } else {
            // No token available - clear tokens but don't redirect if on error page
            clearTokens();
            // Only redirect if not on error page (error page has login button)
            const currentPath = window.location.pathname.toLowerCase();
            if (!currentPath.startsWith('/home/error') && currentPath !== '/home/error') {
                redirectToLogin();
            }
            const error: any = new Error('Authentication required. Please login.');
            error.response = {
                status: 401,
                data: { succeeded: false, message: 'Authentication required. Please login.' }
            };
            throw error;
        }
    }

    if (!response.ok) {
        // Handle 401 errors that weren't caught above (e.g., from retry)
        if (response.status === 401) {
            clearTokens();
            // Only redirect if not on error page (error page has login button)
            const currentPath = window.location.pathname.toLowerCase();
            if (!currentPath.startsWith('/home/error') && currentPath !== '/home/error') {
                redirectToLogin();
            }
        }
        const error: any = new Error(`HTTP error! status: ${response.status}`);
        error.response = {
            status: response.status,
            data: null
        };
        try {
            const responseData = await response.json();
            error.response.data = responseData;
        } catch (e) {
            // Ignore if response is not JSON
        }
        throw error;
    }

    const contentType = response.headers.get("content-type");
    let data: ApiResponse<T>;
    if (contentType && contentType.indexOf("application/json") !== -1) {
        data = await response.json();
    } else {
        data = { succeeded: true } as ApiResponse<T>;
    }

    return data;
}

export const api = {
    async get<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
        return request<T>(url, { ...options, method: 'GET' });
    },

    async post<T>(url: string, data: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
        return request<T>(url, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });
    },

    async put<T>(url: string, data: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
        return request<T>(url, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });
    },

    async delete<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
        return request<T>(url, { ...options, method: 'DELETE' });
    },

    // Token management functions
    setTokens,
    clearTokens,
    getAccessToken,
    getRefreshToken,

    // Session keep-alive
    startSlidingRefresh,
    stopSlidingRefresh,

    // optional: expose the request function
    request,
};

export default api;
